import { FC, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Account } from 'shared/types/account';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { useAccount } from 'frontastic';
import PrintButton from './printButton';

type ThankYouHeaderProps = {
  email?: Account['email'];
  onPrint: (e: React.FormEvent) => void;
  deliveryOption?: string;
  customerName?: string;
  pickup?: string;
  confirmed?: boolean;
};

const ThankYouHeader: FC<ThankYouHeaderProps> = ({
  email,
  deliveryOption,
  onPrint,
  customerName,
  pickup,
  confirmed,
}) => {
  const { formatMessage } = useFormat({ name: 'thank-you' });
  const { loggedIn } = useAccount();
  const textStyle = 'text-14 font-medium leading-loose text-primary-black md:text-16';
  const showSubtitle = deliveryOption !== 'StoreCollection' || loggedIn;

  const deliveryMessage = useMemo(() => {
    if (!pickup) return <Skeleton className="h-48" />;

    if (pickup === '2 hours') {
      return (
        <div className={textStyle}>
          <div className="font-normal">
            {formatMessage({
              id: 'collect.in.two.hours',
              defaultMessage: 'Your order will be available to collect at your chosen store within 2 hours',
            })}
          </div>
          <div className="font-normal">
            {formatMessage({
              id: 'note.store.opening.hours',
              defaultMessage:
                'Please take note of your chosen stores opening hours below, including any Bank Holidays before collecting',
            })}
          </div>
        </div>
      );
    }

    if (pickup === '2 working days') {
      return (
        <div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'in.two.working.days.1',
              defaultMessage: 'Your order will be available to collect at your chosen store within 2 working days',
            })}
          </div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'in.two.working.days.2',
              defaultMessage:
                'Orders placed on Friday before 6pm will be available for collection on Tuesday, excluding bank holidays, when it will be available on Wednesday',
            })}
          </div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'in.two.working.days.3',
              defaultMessage: 'Orders placed after 6pm on Fridays will be available on the following Wednesday',
            })}
          </div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'in.two.working.days.4',
              defaultMessage: 'Please ensure you wait until the scheduled day of collection and present photo ID',
            })}
          </div>
        </div>
      );
    }

    if (pickup !== '2 working days' && pickup !== '2 hours') {
      return (
        <div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'custom.fit.delivery.1',
              defaultMessage:
                'Your order will be available to collect at your chosen store at the estimated time schedule: ',
            })}
            <span className="font-normal">{pickup}</span>
          </div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'custom.fit.delivery.2',
              defaultMessage:
                'You will receive notification when your order is ready to be collected from your chosen store',
            })}
          </div>
          <div className="mb-12 font-normal">
            {formatMessage({
              id: 'custom.fit.delivery.3',
              defaultMessage:
                'Please take note of your chosen stores opening hours below including any Bank Holidays before collecting',
            })}
          </div>
        </div>
      );
    }

    return null;
  }, [pickup, textStyle, formatMessage]);

  return (
    <div className="grid justify-items-center gap-24 border-b border-neutral-400 pb-24 pt-16 md:border-b-0 lg:justify-items-start lg:p-0">
      {/* Title */}
      <Typography
        as="h3"
        asSkeleton={!email}
        className={`text-primary-black md:text-18 lg:text-22 ${email ? 'leading-tight' : 'leading-loose'}`}
      >
        {formatMessage({ id: 'thank.for.order', defaultMessage: 'Thank you for your order' })}
      </Typography>

      {/* Subtitle */}
      {showSubtitle && (
        <div className="flex flex-col items-center gap-5 md:flex-row">
          <Typography asSkeleton={!email} className="text-14 leading-loose text-primary-black md:text-16">
            {formatMessage({ id: 'email.sent', defaultMessage: 'An email confirmation has been sent to' })}
          </Typography>
          <Typography asSkeleton={!email} className={textStyle}>
            {email ?? 'example@email.com'}
          </Typography>
        </div>
      )}
      <PrintButton asSkeleton={!email} onPrint={onPrint} className="w-full py-8 md:w-fit md:px-68 lg:hidden" />
      <div className="w-[100%] py-16 lg:border-y lg:border-neutral-400">
        <Typography className="mb-7 w-fit text-14 font-semibold leading-loose md:text-16" asSkeleton={!customerName}>
          {customerName
            ? `${customerName}, ${formatMessage({
                id: 'your.order.details.are.listed',
                defaultMessage: 'your order details are listed below.',
              })}`
            : ''}
        </Typography>
        <div className="mb-30">
          {!email ? (
            <Skeleton />
          ) : (
            <>
              <Typography as="span" className="font-normal">
                {formatMessage({
                  id: 'your.order.details.description',
                  defaultMessage: `By providing us with your details you are opting in to marketing communications, if you do not wish to be contacted please update ${' '}`,
                })}
              </Typography>
              <a href="#marketing-preference" className="cursor-pointer text-ag_color-text_green underline">
                here.
              </a>
            </>
          )}
        </div>
        {deliveryOption === 'StoreCollection' && (
          <>
            <Typography className="mb-7 w-fit text-14 font-semibold leading-loose text-secondary-black md:text-16">
              {formatMessage({ id: 'what.happens.next', defaultMessage: 'What happens next?' })}
            </Typography>
            {deliveryMessage}
          </>
        )}
        {!confirmed && loggedIn && (
          <>
            <Typography className="mb-7 w-fit text-14 font-medium leading-loose text-secondary-black md:text-16">
              {formatMessage({
                id: 'account.not.verified',
                defaultMessage:
                  'Your account is not yet verified. Please verify your account through the email link sent.',
              })}
            </Typography>
          </>
        )}
      </div>
    </div>
  );
};

export default ThankYouHeader;
